import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    BUILDING_NAMES,
    DEFAULT_SCIENCES,
    RESOURCE_NAMES,
    TEAM_PLANET_SHIP_UPGRADE_QUEUE_LIMIT,
    UPGRADABLE_SHIP_IDS,
    getShipDefinition,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import { canCancelTeamPlanetShipUpgrade, extractBuildingLevels, extractScienceLevels, resolveTeamPlanetShipUpgrades } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import { useUniverseStore } from '../store/universeStore';
import { useTeamPlanetSession } from '../contexts/TeamPlanetContext';
import { usePlanetView } from '../contexts/PlanetViewContext';
import { useNow } from '../hooks/useNow';
import { useStoreTeamPlanet } from '../hooks/useTeamPlanet';
import { cancelTeamPlanetShipUpgrade, queueTeamPlanetShipUpgrade } from '../services/teamPlanet.service';
import ShipUpgradeCard from '../components/ShipUpgradeCard';
import PageLoadingState from '../components/PageLoadingState';
import { Panel } from '../components/ui/Panel';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { zoneAccentStyle } from '../components/planet/zoneAccent';
import { formatFullNumber } from '../utils/formatters';
import { RACE_LABELS } from '../utils/teamPlanetStyle';
import { cn } from '../utils/cn';

/**
 * The Team planet's Tech Center: the alliance's own ship upgrade levels for the
 * viewer's race, paid for by the Team planet. Any member queues; the leader,
 * or the member who queued it and every higher Level it would also cancel,
 * may cancel an upgrade. There is no Rush.
 */
export default function TeamPlanetStarportPage() {
    const { teamPlanet, isLeader } = useTeamPlanetSession();
    const view = usePlanetView();
    const store = useStoreTeamPlanet();
    const { profile } = useAuthStore();
    const { speedMultiplier } = useUniverseStore();
    const now = useNow();
    const race = profile?.race;

    const buildingLevels = useMemo(() => extractBuildingLevels(teamPlanet.buildings), [teamPlanet.buildings]);
    const scienceLevels = useMemo(
        () => extractScienceLevels(teamPlanet.sciences ?? DEFAULT_SCIENCES),
        [teamPlanet.sciences],
    );
    const upgradeLevels = useMemo(
        () => (race ? resolveTeamPlanetShipUpgrades(teamPlanet, race) : {}),
        [teamPlanet, race],
    );
    const laneQueue = useMemo(
        () => (teamPlanet.tickCheckpoints ?? []).filter(
            (e): e is TickCheckpointEventOf<'shipUpgrade'> =>
                e.eventType === 'shipUpgrade' && !e.cancelled && e.eventDetails.queueLane === race,
        ),
        [teamPlanet.tickCheckpoints, race],
    );
    const resources = useMemo(() => ({
        metal: teamPlanet.resources.metal.current,
        minerals: teamPlanet.resources.minerals.current,
        gas: teamPlanet.resources.gas.current,
        energy: teamPlanet.resources.energy.rate - (teamPlanet.resources.energy.consumed ?? 0),
        ozone: teamPlanet.state.ozone.current,
    }), [teamPlanet.resources, teamPlanet.state.ozone]);

    if (!race) return <PageLoadingState message={`Loading ${BUILDING_NAMES.techCenter}…`} />;

    const techCenterLevel = buildingLevels.techCenter ?? 0;
    const queueFull = laneQueue.length >= TEAM_PLANET_SHIP_UPGRADE_QUEUE_LIMIT;

    return (
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4" style={zoneAccentStyle('military')} data-testid="team-planet-starport-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-display text-content-primary">{BUILDING_NAMES.techCenter}</h1>
                    <p className="text-body text-content-muted">
                        The alliance&apos;s upgrades for {RACE_LABELS[race]} ships, paid for by the Team planet. Every {RACE_LABELS[race]} member can queue them.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {techCenterLevel > 0 && (
                        <span className="text-content-muted text-body tabular-nums" data-testid="team-planet-starport-queue">
                            Queue: {laneQueue.length} / {TEAM_PLANET_SHIP_UPGRADE_QUEUE_LIMIT}
                        </span>
                    )}
                    <Link to="/team-planet" className={linkButtonClasses('secondary')}>← Back to Team Planet</Link>
                </div>
            </div>

            {techCenterLevel === 0 ? (
                <Panel variant="solid" padding="md" className="flex flex-col gap-2" data-testid="team-planet-no-starport">
                    <h2 className="text-title text-content-primary">No {BUILDING_NAMES.techCenter} yet</h2>
                    <p className="text-body text-content-muted">Construct the {BUILDING_NAMES.techCenter} on the Team planet to upgrade the alliance&apos;s ships.</p>
                    <Link to={view.zoneRoute('military', 'techCenter')} className={cn(linkButtonClasses('secondary', 'sm'), 'self-start')}>
                        Go to the Military zone
                    </Link>
                </Panel>
            ) : (
                <>
                    <p className="flex flex-wrap gap-x-4 text-content-muted text-label tabular-nums">
                        <span>{RESOURCE_NAMES.metal}: <span data-testid="team-planet-starport-metal">{formatFullNumber(Math.floor(resources.metal))}</span></span>
                        <span>{RESOURCE_NAMES.minerals}: <span data-testid="team-planet-starport-minerals">{formatFullNumber(Math.floor(resources.minerals))}</span></span>
                        <span>{RESOURCE_NAMES.gas}: <span data-testid="team-planet-starport-gas">{formatFullNumber(Math.floor(resources.gas))}</span></span>
                    </p>
                    <div className="flex flex-col gap-3">
                        {UPGRADABLE_SHIP_IDS.map((shipId) => {
                            const def = getShipDefinition(race, shipId);
                            return (
                                <ShipUpgradeCard
                                    key={shipId}
                                    def={def}
                                    race={race}
                                    resources={resources}
                                    techCenterLevel={techCenterLevel}
                                    speedMultiplier={speedMultiplier}
                                    scienceLevels={scienceLevels}
                                    buildingLevels={buildingLevels}
                                    currentLevel={upgradeLevels[shipId] ?? 0}
                                    queuedEvents={laneQueue.filter((e) => e.eventDetails.shipType === shipId)}
                                    queueFull={queueFull}
                                    now={now}
                                    onUpgrade={async () => { await store(await queueTeamPlanetShipUpgrade(def.id)); }}
                                    canCancel={(event) => !!profile && canCancelTeamPlanetShipUpgrade(teamPlanet.tickCheckpoints ?? [], event.eventId, profile.id, isLeader)}
                                    onCancel={async (event) => { await store(await cancelTeamPlanetShipUpgrade(event.eventId)); }}
                                />
                            );
                        })}
                    </div>
                </>
            )}
        </main>
    );
}
